import React, { useState, useEffect, useMemo } from 'react';
import {
  Table,
  Card,
  Select,
  message,
  Typography,
  Row,
  Col,
  Spin,
  Button,
  Space,
  Pagination,
  Empty,
  Tag,
  Tabs,
  Input,
  List,
  Modal,
  Descriptions,
} from 'antd';
import {
  TrophyOutlined,
  ReloadOutlined,
  SearchOutlined,
  EnvironmentOutlined,
  CalendarOutlined,
  ArrowLeftOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { useDebounce } from '../hooks/useDebounce';
import { useCache } from '../hooks/useCache';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL, getAuthHeaders } from '../api';
import { formatDisplayDate } from '../utils/formatDisplayDate';
import { sortResultsByRaceDate } from '../utils/sortResultsByRaceDate';
import { podiumPlaceTagColor } from '../utils/podiumPlaceTagColor';
import type {
  Result,
  Competition,
  CompetitionAthlete,
  CompetitionsResponse,
  ResultsResponse,
  CompetitionAthletesResponse,
} from '../types';

const { Title, Text } = Typography;

function CompetitionDetailFields({ c }: { c: Competition }) {
  const venueLine = [c.venue, c.area].filter(Boolean).join(' · ') || '—';
  const counts =
    typeof c.athlete_count === 'number' || typeof c.result_count === 'number'
      ? `${c.athlete_count ?? '—'} athletes · ${c.result_count ?? '—'} results`
      : '—';

  return (
    <Descriptions bordered column={1} size="small">
      <Descriptions.Item label="Name">{c.name}</Descriptions.Item>
      <Descriptions.Item label="Venue">{venueLine}</Descriptions.Item>
      <Descriptions.Item label="Country">{c.country_name || c.country_code || '—'}</Descriptions.Item>
      <Descriptions.Item label="Dates">
        {formatDisplayDate(c.start_date)}
        {c.end_date ? ` – ${formatDisplayDate(c.end_date)}` : ''}
      </Descriptions.Item>
      <Descriptions.Item label="Athletes / results">{counts}</Descriptions.Item>
    </Descriptions>
  );
}

type CompetitionDetailModalProps = {
  competition: Competition | null;
  open: boolean;
  isMobile: boolean;
  onClose: () => void;
  onViewResults: (competitionId: number) => void;
};

/**
 * Modal showing full competition metadata; primary action opens results/athletes for that meet.
 */
function CompetitionDetailModal({
  competition,
  open,
  isMobile,
  onClose,
  onViewResults,
}: CompetitionDetailModalProps) {
  if (!competition) return null;

  return (
    <Modal
      title="Competition details"
      open={open}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>,
      ]}
      width={isMobile ? '95%' : 560}
      destroyOnClose
    >
      <CompetitionDetailFields c={competition} />
    </Modal>
  );
}

const Results: React.FC = () => {
  const { user } = useAuth();
  const [isMobile, setIsMobile] = useState(false);
  const [selectedCompetition, setSelectedCompetition] = useState<number | null>(null);
  const [resultsPage, setResultsPage] = useState(1);
  const [athletesPage, setAthletesPage] = useState(1);
  const [activeTab, setActiveTab] = useState('results');
  const [searchText, setSearchText] = useState('');
  const [competitionsPage, setCompetitionsPage] = useState(1);
  const [hasCompetitionSearch, setHasCompetitionSearch] = useState(false);
  const [resultsSearchText, setResultsSearchText] = useState('');
  const [resultsSearchType, setResultsSearchType] = useState<'athlete' | 'event'>('athlete');
  const [detailCompetition, setDetailCompetition] = useState<Competition | null>(null);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const debouncedSearch = useDebounce(searchText, 300);

  const { data: competitionsData, loading: loadingCompetitions, error: competitionsError } =
    useCache<CompetitionsResponse>(
      `competitions-user-${user?.uid ?? 'none'}-page-${competitionsPage}-search-${debouncedSearch}-hassearch-${hasCompetitionSearch ? 'yes' : 'no'}`,
      async () => {
        if (!hasCompetitionSearch) {
          return { competitions: [], pagination: { page: 1, per_page: 20, total: 0, pages: 0 } };
        }
        const headers = await getAuthHeaders();
        if (!headers.Authorization) {
          return { competitions: [], pagination: { page: 1, per_page: 20, total: 0, pages: 0 } };
        }
        const response = await axios.get(`${API_BASE_URL}/api/competitions`, {
          params: {
            page: competitionsPage,
            per_page: 20,
            search: debouncedSearch || undefined,
          },
          headers,
        });
        return response.data;
      },
      5 * 60 * 1000
    );

  const { data: resultsData, loading: loadingResults, error: resultsError, refresh: refreshResults } =
    useCache<ResultsResponse>(
      `competition-results-user-${user?.uid ?? 'none'}-${selectedCompetition}-page-${resultsPage}`,
      async () => {
        if (!selectedCompetition)
          return { results: [], competition_id: 0, page: 1, per_page: 100, total: 0 };
        const headers = await getAuthHeaders();
        if (!headers.Authorization)
          return { results: [], competition_id: 0, page: 1, per_page: 100, total: 0 };
        const response = await axios.get(
          `${API_BASE_URL}/api/competitions/${selectedCompetition}/results`,
          { params: { page: resultsPage, per_page: 100 }, headers }
        );
        return response.data;
      },
      2 * 60 * 1000
    );

  const { data: athletesData, loading: loadingAthletes, refresh: refreshAthletes } =
    useCache<CompetitionAthletesResponse>(
      `competition-athletes-user-${user?.uid ?? 'none'}-${selectedCompetition}-page-${athletesPage}`,
      async () => {
        if (!selectedCompetition)
          return {
            athletes: [],
            competition_id: 0,
            page: 1,
            per_page: 100,
            total: 0,
            pagination: { page: 1, per_page: 100, total: 0, pages: 0 },
          };
        const headers = await getAuthHeaders();
        if (!headers.Authorization)
          return {
            athletes: [],
            competition_id: 0,
            page: 1,
            per_page: 100,
            total: 0,
            pagination: { page: 1, per_page: 100, total: 0, pages: 0 },
          };
        const response = await axios.get(
          `${API_BASE_URL}/api/competitions/${selectedCompetition}/athletes`,
          { params: { page: athletesPage, per_page: 100 }, headers }
        );
        return response.data;
      },
      2 * 60 * 1000
    );

  const handleCompetitionSelect = (competitionId: number) => {
    setDetailCompetition(null);
    setSelectedCompetition(competitionId);
    setResultsPage(1);
    setAthletesPage(1);
    setActiveTab('results');
    setResultsSearchText('');
  };

  const selectedCompetitionInfo = useMemo(() => {
    if (!competitionsData?.competitions || !selectedCompetition) return null;
    return competitionsData.competitions.find((c) => c.id === selectedCompetition);
  }, [competitionsData?.competitions, selectedCompetition]);

  const filteredResults = useMemo(() => {
    if (!resultsData?.results) return [];
    if (!resultsSearchText.trim()) return sortResultsByRaceDate(resultsData.results);
    const searchLower = resultsSearchText.toLowerCase().trim();
    const searchFilteredResults = resultsData.results.filter((result: Result) => {
      if (resultsSearchType === 'athlete') {
        return result.athlete_name?.toLowerCase().includes(searchLower);
      } else {
        return (
          result.event_name?.toLowerCase().includes(searchLower) ||
          result.discipline_name?.toLowerCase().includes(searchLower)
        );
      }
    });
    return sortResultsByRaceDate(searchFilteredResults);
  }, [resultsData?.results, resultsSearchText, resultsSearchType]);

  useEffect(() => {
    if (competitionsError) message.error(`Failed to fetch competitions: ${competitionsError}`);
  }, [competitionsError]);

  useEffect(() => {
    if (resultsError) message.error(`Failed to fetch results: ${resultsError}`);
  }, [resultsError]);

  const resultColumns = [
    {
      title: 'Place',
      dataIndex: 'place',
      key: 'place',
      width: 80,
      render: (place: number) => (
        <Tag color={podiumPlaceTagColor(place)}>{place}</Tag>
      ),
      sorter: (a: Result, b: Result) => a.place - b.place,
    },
    {
      title: 'Athlete',
      dataIndex: 'athlete_name',
      key: 'athlete_name',
      width: 150,
      ellipsis: true,
      sorter: (a: Result, b: Result) => a.athlete_name.localeCompare(b.athlete_name),
      render: (text: string) => (
        <span style={{ wordWrap: 'break-word', overflowWrap: 'break-word' }} title={text}>
          {text}
        </span>
      ),
    },
    {
      title: 'Country',
      dataIndex: 'athlete_country',
      key: 'athlete_country',
      width: 100,
    },
    {
      title: 'Event',
      dataIndex: 'event_name',
      key: 'event_name',
      width: 180,
      ellipsis: true,
      sorter: (a: Result, b: Result) => a.event_name.localeCompare(b.event_name),
      render: (text: string) => (
        <span title={text} style={{ fontSize: '12px' }}>
          {text}
        </span>
      ),
    },
    {
      title: 'Discipline',
      dataIndex: 'discipline_name',
      key: 'discipline_name',
      width: 180,
      ellipsis: true,
      render: (text: string, record: Result) => (
        <Space direction="vertical" size={0}>
          <span title={text} style={{ fontSize: '12px' }}>
            {text || record.event_name}
          </span>
          <Tag color="blue" style={{ fontSize: '10px', height: 'auto', lineHeight: '18px' }}>
            {record.category || 'Track & Field'}
          </Tag>
        </Space>
      ),
      sorter: (a: Result, b: Result) =>
        (a.discipline_name || a.event_name).localeCompare(b.discipline_name || b.event_name),
    },
    {
      title: 'Result',
      dataIndex: 'mark',
      key: 'mark',
      width: 100,
      render: (text: string) => <span style={{ fontFamily: 'monospace' }}>{text}</span>,
    },
    {
      title: 'Race Date',
      dataIndex: 'race_date',
      key: 'race_date',
      width: 120,
      render: (date: string) => new Date(date).toLocaleDateString(),
      defaultSortOrder: 'descend' as const,
      sorter: (a: Result, b: Result) =>
        new Date(a.race_date).getTime() - new Date(b.race_date).getTime(),
    },
    {
      title: 'Race Type',
      dataIndex: 'race_type',
      key: 'race_type',
      width: 100,
    },
  ];

  const athleteColumns = [
    {
      title: 'Athlete',
      dataIndex: 'full_name',
      key: 'full_name',
      width: 200,
      ellipsis: true,
      sorter: (a: CompetitionAthlete, b: CompetitionAthlete) =>
        a.full_name.localeCompare(b.full_name),
      render: (text: string, record: CompetitionAthlete) => (
        <div>
          <div style={{ fontWeight: 'bold', fontSize: '14px' }}>{text}</div>
          <div style={{ fontSize: '12px', color: 'var(--black)' }}>
            {record.country_name || record.country_code} • {record.gender}
          </div>
        </div>
      ),
    },
    {
      title: 'Results',
      dataIndex: 'result_count',
      key: 'result_count',
      width: 100,
      sorter: (a: CompetitionAthlete, b: CompetitionAthlete) => a.result_count - b.result_count,
      render: (count: number) => <Tag color="blue">{count}</Tag>,
    },
    {
      title: 'Best Place',
      dataIndex: 'best_place',
      key: 'best_place',
      width: 100,
      sorter: (a: CompetitionAthlete, b: CompetitionAthlete) => a.best_place - b.best_place,
      render: (place: number) => (
        <Tag color={podiumPlaceTagColor(place)}>{place}</Tag>
      ),
    },
    {
      title: 'Events',
      dataIndex: 'events_participated',
      key: 'events_participated',
      width: 100,
      sorter: (a: CompetitionAthlete, b: CompetitionAthlete) =>
        a.events_participated - b.events_participated,
      render: (count: number) => <Tag color="green">{count}</Tag>,
    },
  ];

  return (
    <div className="page-container">
      <Title level={2} style={{ marginBottom: 8, fontSize: isMobile ? '20px' : undefined }}>
        <TrophyOutlined style={{ marginRight: 8, color: 'var(--blue-bell)' }} />
        Competitions
      </Title>
      <Text type="secondary" style={{ marginBottom: 16, display: 'block' }}>
        Open a competition for details, then view results and athletes when you are ready
      </Text>

      {/* Competition list — hidden once one is selected */}
      {!selectedCompetition && (
        <Card
          title={
            <Space style={{ width: '100%', justifyContent: 'space-between', flexWrap: 'wrap' }}>
              <Text strong>Competitions</Text>
              <Space wrap>
                <Input
                  placeholder="Search competitions…"
                  prefix={<SearchOutlined />}
                  value={searchText}
                  onChange={(e) => {
                    const nextSearch = e.target.value;
                    setSearchText(nextSearch);
                    setHasCompetitionSearch(nextSearch.trim().length > 0);
                    setCompetitionsPage(1);
                  }}
                  allowClear
                  size="small"
                  style={{ width: isMobile ? '100%' : 240 }}
                />
              </Space>
            </Space>
          }
          styles={{ body: { padding: isMobile ? '8px 12px' : '12px 24px' } }}
        >
          {loadingCompetitions ? (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <Spin size="large" />
            </div>
          ) : (
            <>
              <List
                dataSource={competitionsData?.competitions ?? []}
                locale={{
                  emptyText: (
                    <Empty
                      description={hasCompetitionSearch ? 'No competitions found' : 'Search for Competitions'}
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                  ),
                }}
                renderItem={(comp) => (
                  <List.Item
                    style={{
                      cursor: 'pointer',
                      padding: '12px 8px',
                      borderRadius: 8,
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--pale-sky)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                    onClick={() => setDetailCompetition(comp)}
                    actions={[
                      <Button
                        type="link"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDetailCompetition(comp);
                        }}
                      >
                        View details
                      </Button>,
                    ]}
                  >
                    <List.Item.Meta
                      title={
                        <Space size={6} wrap>
                          <Text strong style={{ fontSize: isMobile ? 13 : 14 }}>
                            {comp.name}
                          </Text>
                        </Space>
                      }
                      description={
                        <Space size={12} wrap style={{ fontSize: 12, color: 'var(--black)' }}>
                          <Space size={4}>
                            <EnvironmentOutlined />
                            {comp.venue}{comp.country_name ? `, ${comp.country_name}` : ''}
                          </Space>
                          <Space size={4}>
                            <CalendarOutlined />
                            {new Date(comp.start_date).toLocaleDateString()}
                          </Space>
                        </Space>
                      }
                    />
                  </List.Item>
                )}
              />

              {(competitionsData?.pagination?.total ?? 0) > 0 && (
                <div style={{ textAlign: 'center', marginTop: 16 }}>
                  <Pagination
                    current={competitionsPage}
                    total={competitionsData?.pagination?.total ?? 0}
                    pageSize={competitionsData?.pagination?.per_page ?? 20}
                    onChange={(p) => setCompetitionsPage(p)}
                    showSizeChanger={false}
                    showQuickJumper={!isMobile}
                    showTotal={(total, range) => `${range[0]}-${range[1]} of ${total}`}
                    size="small"
                  />
                </div>
              )}
            </>
          )}
        </Card>
      )}

      {/* Results panel — shown after a competition is selected */}
      {selectedCompetition && (
        <>
          <Card
            style={{ marginBottom: 16 }}
            styles={{ body: { padding: '12px 16px' } }}
          >
            <Row align="middle" gutter={[12, 8]}>
              <Col>
                <Button
                  icon={<ArrowLeftOutlined />}
                  onClick={() => setSelectedCompetition(null)}
                  size="small"
                >
                  Back
                </Button>
              </Col>
              <Col flex="auto">
                {selectedCompetitionInfo ? (
                  <Space direction="vertical" size={0}>
                    <Space size={6} wrap>
                      <TrophyOutlined style={{ color: 'var(--racing-red)' }} />
                      <Text strong style={{ fontSize: isMobile ? 13 : 15 }}>
                        {selectedCompetitionInfo.name}
                      </Text>
                    </Space>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      <EnvironmentOutlined style={{ marginRight: 4 }} />
                      {selectedCompetitionInfo.venue}
                      {selectedCompetitionInfo.country_name
                        ? `, ${selectedCompetitionInfo.country_name}`
                        : ''}
                      <CalendarOutlined style={{ marginLeft: 12, marginRight: 4 }} />
                      {new Date(selectedCompetitionInfo.start_date).toLocaleDateString()}
                    </Text>
                  </Space>
                ) : (
                  <Text type="secondary">Loading competition info…</Text>
                )}
              </Col>
            </Row>
          </Card>

          <Card>
            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              size={isMobile ? 'small' : 'middle'}
              items={[
                {
                  key: 'results',
                  label: (
                    <Space>
                      <TrophyOutlined />
                      Results
                      <Tag color="blue">{resultsData?.total || 0}</Tag>
                    </Space>
                  ),
                  children: (
                    <div>
                      <div
                        style={{
                          marginBottom: 16,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          flexWrap: 'wrap',
                          gap: 8,
                        }}
                      >
                        <Space.Compact style={{ flex: 1, minWidth: isMobile ? '100%' : 300 }}>
                          <Select
                            value={resultsSearchType}
                            onChange={setResultsSearchType}
                            style={{ width: 110 }}
                            options={[
                              { label: 'Athlete', value: 'athlete' },
                              { label: 'Event', value: 'event' },
                            ]}
                          />
                          <Input
                            placeholder={`Search by ${resultsSearchType}…`}
                            prefix={<SearchOutlined />}
                            value={resultsSearchText}
                            onChange={(e) => setResultsSearchText(e.target.value)}
                            allowClear
                            style={{ flex: 1 }}
                          />
                        </Space.Compact>
                        <Button
                          icon={<ReloadOutlined />}
                          onClick={refreshResults}
                          loading={loadingResults}
                          size="small"
                        >
                          Refresh
                        </Button>
                      </div>

                      {resultsSearchText && (
                        <div style={{ marginBottom: 12 }}>
                          <Text type="secondary">
                            Showing {filteredResults.length} of {resultsData?.results.length || 0} results
                            {filteredResults.length < (resultsData?.results.length || 0) && (
                              <Button
                                type="link"
                                size="small"
                                onClick={() => setResultsSearchText('')}
                                style={{ padding: 0, marginLeft: 8 }}
                              >
                                Clear filter
                              </Button>
                            )}
                          </Text>
                        </div>
                      )}

                      {loadingResults ? (
                        <div style={{ textAlign: 'center', padding: 50 }}>
                          <Spin size="large" />
                          <div style={{ marginTop: 16 }}>Loading results…</div>
                        </div>
                      ) : resultsData?.results.length === 0 ? (
                        <Empty
                          description="No results found for this competition"
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                        />
                      ) : filteredResults.length === 0 ? (
                        <Empty
                          description={`No results matching "${resultsSearchText}"`}
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                        />
                      ) : (
                        <>
                          <Table
                            columns={resultColumns}
                            dataSource={filteredResults}
                            rowKey="id"
                            pagination={false}
                            scroll={{ x: 1200, y: 400 }}
                            size="small"
                            style={{ marginBottom: 16 }}
                          />
                          {!resultsSearchText &&
                            resultsData &&
                            resultsData.total > resultsData.per_page && (
                              <div style={{ textAlign: 'center' }}>
                                <Pagination
                                  current={resultsPage}
                                  total={resultsData.total}
                                  pageSize={resultsData.per_page}
                                  onChange={setResultsPage}
                                  showSizeChanger={false}
                                  showQuickJumper={!isMobile}
                                  showTotal={(total, range) =>
                                    `${range[0]}-${range[1]} of ${total} results`
                                  }
                                />
                              </div>
                            )}
                        </>
                      )}
                    </div>
                  ),
                },
                {
                  key: 'athletes',
                  label: (
                    <Space>
                      <TrophyOutlined />
                      Athletes
                      <Tag color="green">{athletesData?.total || 0}</Tag>
                    </Space>
                  ),
                  children: (
                    <div>
                      <div style={{ textAlign: 'right', marginBottom: 16 }}>
                        <Button
                          icon={<ReloadOutlined />}
                          onClick={refreshAthletes}
                          loading={loadingAthletes}
                          size="small"
                        >
                          Refresh Athletes
                        </Button>
                      </div>

                      {loadingAthletes ? (
                        <div style={{ textAlign: 'center', padding: 50 }}>
                          <Spin size="large" />
                          <div style={{ marginTop: 16 }}>Loading athletes…</div>
                        </div>
                      ) : athletesData?.athletes.length === 0 ? (
                        <Empty
                          description="No athletes found for this competition"
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                        />
                      ) : (
                        <>
                          <Table
                            columns={athleteColumns}
                            dataSource={athletesData?.athletes || []}
                            rowKey="id"
                            pagination={false}
                            scroll={{ x: 800, y: 400 }}
                            size="small"
                            style={{ marginBottom: 16 }}
                          />
                          {athletesData && (athletesData.pagination?.pages ?? 0) > 1 && (
                            <div style={{ textAlign: 'center' }}>
                              <Pagination
                                current={athletesPage}
                                total={athletesData.pagination?.total ?? 0}
                                pageSize={athletesData.pagination?.per_page ?? 100}
                                onChange={setAthletesPage}
                                showSizeChanger={false}
                                showQuickJumper={!isMobile}
                                showTotal={(total, range) =>
                                  `${range[0]}-${range[1]} of ${total} athletes`
                                }
                              />
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ),
                },
              ]}
            />
          </Card>
        </>
      )}

      <CompetitionDetailModal
        competition={detailCompetition}
        open={!!detailCompetition}
        isMobile={isMobile}
        onClose={() => setDetailCompetition(null)}
        onViewResults={handleCompetitionSelect}
      />
    </div>
  );
};

export default Results;
